<template>
  <div>
    <DialogBottomSheet
      :is-visible="isVisible"
      :width="isMobile ? '100%' : '360px'"
      @dialog-bottom-sheet:change-visible="isVisible = $event"
    >
      <v-card
        class="doctor-contacts"
        :class="{ 'doctor-contacts_mobile': isMobile }"
      >
        <div
          class="d-flex align-center"
          :class="isMobile ? 'px-4 pt-6' : 'pa-2'"
        >
          <v-btn
            v-if="!isMobile"
            icon
            text
            class="mr-4"
            color="icon-secondary"
            data-qa="base_modal_close"
            @click="isVisible = false"
          >
            <v-icon>
              ui-icon-close-not-a-circle
            </v-icon>
          </v-btn>
          <div class="text-h6 text--text">
            {{ title }}
          </div>
        </div>
        <v-divider v-if="!isMobile" />
        <div class="doctor-contacts__body">
          <DoctorContactsSkeleton
            v-if="isLoading"
            class="pt-3 pb-4"
          />
          <template v-else>
            <DoctorContactsList
              :doctor="doctor"
              :contacts-list="contactsList"
              class="pt-4 pb-2"
            />
            <div class="text-caption icon-secondary--text px-4 pb-6">
              {{ description }}
            </div>
          </template>
        </div>
      </v-card>
    </DialogBottomSheet>

    <UserAuthorization
      v-if="!useAuthMt"
      :is-visible="isVisibleAuth"
      :persistent="false"
      is-render-in-modal
      @modal:update-visible="isVisibleAuth = $event"
      @authorization:confirmed="handleAuthConfirm"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import I18NextVue from 'i18next'
import JSON5 from 'process-js-objects'
import YaGoals from 'www/modules/YandexGoals'
import { dispatchCustomEventGlobally } from 'www/utils'
import DialogBottomSheet from 'www/vue_components/common/core/DialogBottomSheet.vue'
import UserAuthorization from 'www/vue_components/common/UserAuthorization/UserAuthorization.vue'
import apiDoctorContacts from 'www/vue_components/common/DoctorContacts/api/apiDoctorContacts'
import DoctorContactsList from 'www/vue_components/common/DoctorContacts/components/DoctorContactsList.vue'
import DoctorContactsSkeleton from 'www/vue_components/common/DoctorContacts/components/DoctorContactsSkeleton.vue'
import type { ApiDoctorContactsResponseGet } from 'www/vue_components/common/DoctorContacts/api/apiDoctorContacts.types'
import { EVENTS as SNACKBAR_EVENTS } from 'www/vue_components/common/core/SnackbarNotification/constants'

const useAuthMt = !window.IS_YDOC
const instanceYaGoals = new YaGoals()
const isMobile = window.MOBILE_VERSION
const isVisible = ref(false)
const isVisibleAuth = ref(false)
const isUserAnonymous = ref(window.USER.isAnonymous)
const isLoading = ref(false)
const doctor = ref({
  id: 0,
  fio: '',
})
const contactsList = ref<ApiDoctorContactsResponseGet>([])
const { t, language } = I18NextVue
const title = t('Контакты врача', {
  ns: language,
})
const description = t('Facebook, Instagram, WhatsApp — продукты компании Meta, которая признана экстремистской организацией и деятельность которой запрещена на территории РФ', {
  ns: language,
})

window.addEventListener('click', (event: MouseEvent) => {
  const openerButton = event.target instanceof HTMLElement
    && event.target.closest('[data-open-doctor-contacts]')

  if (!openerButton) {
    return
  }

  const { mtSSORedirectUrl, doctor: doctorRaw } = JSON5.parse(openerButton.getAttribute('data-open-doctor-contacts') ?? '{}')

  doctor.value = doctorRaw

  if (isUserAnonymous.value) {
    instanceYaGoals.send({ name: 'doctorContacts__openAsAnonym' })

    if (useAuthMt) {
      window.location.assign(mtSSORedirectUrl)
    } else {
      isVisibleAuth.value = true
    }

    return
  }

  instanceYaGoals.send({ name: 'doctorContacts__openAsAuth' })

  isVisible.value = true

  if (!contactsList.value.length) {
    loadContactsList()
  }
})

async function loadContactsList() {
  try {
    isLoading.value = true

    const { data } = await apiDoctorContacts.get({
      doctor_id: doctor.value.id,
    })

    contactsList.value = data
  } catch {
    isVisible.value = false

    dispatchCustomEventGlobally(SNACKBAR_EVENTS.open, {
      upper: true,
      message: 'Произошла ошибка. Попробуйте ещё раз',
    })
  } finally {
    isLoading.value = false
  }
}

function handleAuthConfirm() {
  isVisibleAuth.value = false
  isUserAnonymous.value = false
  isVisible.value = true

  if (!contactsList.value.length) {
    loadContactsList()
  }
}
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/mixins';

.doctor-contacts {
  &_mobile {
    max-height: 60dvh;
    overflow-x: auto;
  }

  &:not(&_mobile) &__body {
    max-height: calc(100dvh - 150px);
    overflow-x: auto;

    @include custom-scroll;
  }
}
</style>
